import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { NextcloudIcon } from '@/components/icons/nextcloud-icon'
import { fetchWithAuth } from '@/lib/api'
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import { testIds } from '@/lib/test-ids'
import { nextcloudWebUrl } from './nextcloud-entry'

const ExternalIcon = resolveOpenExternalIcon(NextcloudIcon)

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="break-words text-sm text-foreground">{value}</div>
    </div>
  )
}

export function NextcloudDocumentInfoPane({
  name,
  path,
  fileId,
  organizationId,
}: {
  name: string
  path: string
  fileId?: string
  organizationId?: string
}) {
  const [instanceUrl, setInstanceUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationId) return
    let cancelled = false
    void (async () => {
      try {
        const response = await fetchWithAuth(
          `/api/nextcloud/status?organizationId=${encodeURIComponent(organizationId)}`,
        )
        if (!response.ok) return
        const data = (await response.json()) as { instanceUrl?: string }
        if (!cancelled) setInstanceUrl(data.instanceUrl || null)
      } catch {
        if (!cancelled) setInstanceUrl(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [organizationId])

  const webUrl = nextcloudWebUrl(instanceUrl, fileId)

  return (
    <div className="space-y-4 p-4" data-testid={testIds.nextcloud.documentInfo}>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Title
        </div>
        <h4 className="mt-0.5 text-[1.0625rem] font-semibold leading-snug text-foreground">
          {name}
        </h4>
      </div>
      <MetaRow label="Path" value={path} />
      <MetaRow label="File id" value={fileId} />
      {webUrl ? (
        <Button size="sm" variant="outline" className="w-full justify-start gap-2" asChild>
          <a href={webUrl} target="_blank" rel="noopener noreferrer">
            <ExternalIcon className="h-4 w-4" />
            Open in Nextcloud
          </a>
        </Button>
      ) : null}
    </div>
  )
}
